"use client";

/* eslint-disable react/function-component-definition */
/* eslint-disable @typescript-eslint/no-empty-interface */
import { HashtagNode } from "@lexical/hashtag";
import { AutoLinkNode, LinkNode } from "@lexical/link";
import { ListItemNode, ListNode } from "@lexical/list";
import { OverflowNode } from "@lexical/overflow";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { ClearEditorPlugin } from "@lexical/react/LexicalClearEditorPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import LexicalErrorBoundary from "@lexical/react/LexicalErrorBoundary";
import { HashtagPlugin } from "@lexical/react/LexicalHashtagPlugin";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { HorizontalRuleNode } from "@lexical/react/LexicalHorizontalRuleNode";
import { HorizontalRulePlugin } from "@lexical/react/LexicalHorizontalRulePlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import ClickableLinkPlugin from "@lexical/react/LexicalClickableLinkPlugin";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { TabIndentationPlugin } from "@lexical/react/LexicalTabIndentationPlugin";
import { QuoteNode } from "@lexical/rich-text";
import EditorPlaceholder from "components/editor/EditorPlaceholder";
import LexicalAutoLinkPlugin from "components/editor/plugins/AutoLinkPlugin";
import { CharacterLimitPlugin } from "components/editor/plugins/CharacterLimitPlugin";
import ListMaxIndentLevelPlugin from "components/editor/plugins/ListMaxIndentLevelPlugin";
import MarkdownPlugin from "components/editor/plugins/MarkdownShortcutPlugin";
import TabFocusPlugin from "components/editor/plugins/TabFocusPlugin";
import ToolbarPlugin from "components/editor/plugins/ToolbarPlugin";
import classNames from "components/ui/classNames";
import { $getRoot } from "lexical";
import type { Klass, LexicalNode } from "lexical";
import { memo, useState } from "react";
import LinkPlugin from "components/editor/plugins/LinkPlugin";
import FloatingLinkEditorPlugin from "components/editor/plugins/FloatingLinkEditorPlugin";
import SubmitPlugin from "components/editor/plugins/SubmitPlugin";
import ResetEditorPlugin from "components/editor/plugins/ResetEditorPlugin";

// TODO: Aparentemente, é utilizado para resetar o state do Editor quando usado via form. Verificar se é possível remover e melhorar
// a forma como isso é feito
export const EDITOR_INITIAL_STATE =
  '{"root":{"children":[{"children":[],"direction":null,"format":"","indent":0,"type":"paragraph","version":1}],"direction":null,"format":"","indent":0,"type":"root","version":1}}';

interface EditorProps {
  className?: string;
  placeholder?: string;
  editable?: boolean;
  initialState?: string;
  onChange?: (bodyAsJson: string, bodyAsPlainText: string) => void;
  onBlur?: () => void;
  onClear?: () => void;
  onSubmit?: () => void;
}

const EditorNodes: Klass<LexicalNode>[] = [
  ListNode,
  ListItemNode,
  QuoteNode,
  AutoLinkNode,
  LinkNode,
  OverflowNode,
  HashtagNode,
  HorizontalRuleNode,
];

const Editor = ({
  className,
  placeholder = "Escreva algo brilhante aqui...",
  editable = true,
  initialState,
  onChange,
  onBlur,
  onClear,
  onSubmit,
}: EditorProps) => {
  const [floatingAnchorElem, setFloatingAnchorElem] = useState<HTMLDivElement | null>(null);
  const [isLinkEditMode, setIsLinkEditMode] = useState<boolean>(false);

  const onRef = (_floatingAnchorElem: HTMLDivElement) => {
    if (_floatingAnchorElem !== null) {
      setFloatingAnchorElem(_floatingAnchorElem);
    }
  };

  return (
    <LexicalComposer
      initialConfig={{
        editable,
        nodes: [...EditorNodes],
        onError: (err: Error) => {
          throw err;
        },
        namespace: "Fundamentei",
        theme: {
          hashtag: "bg-blue-100",
          quote: "border-l-4 border-slate-200 pl-2.5 text-slate-700",
          paragraph: "text-gray-900",
          link: "text-blue-600",
          text: {
            underline: "underline",
            strikethrough: "line-through",
            italic: "italic",
            underlineStrikethrough: "underline line-through",
          },
          list: {
            olDepth: [
              "m-0 p-0 list-inside",
              "m-0 p-0 list-inside list-[upper-alpha]",
              "m-0 p-0 list-inside list-[lower-alpha]",
              "m-0 p-0 list-inside list-[upper-roman]",
              "m-0 p-0 list-inside list-[lower-roman]",
            ],
            ulDepth: [
              "m-0 p-0 list-inside",
              "m-0 p-0 list-inside",
              "m-0 p-0 list-inside",
              "m-0 p-0 list-inside",
              "m-0 p-0 list-inside",
            ],
            listitem: "list-item",
            ul: "list-disc list-inside p-0",
            ol: "list-decimal list-inside p-0",
          },
        },
        editorState: initialState,
      }}
    >
      <div
        className={classNames(
          "bg-white font-aeonik font-normal",
          {
            "rounded-md border border-slate-200 p-4": editable,
          },
          className,
        )}
      >
        {editable && (
          // <div className="sticky top-[161px] z-10 mb-4 border-b border-slate-100 bg-white pb-2">
          <div className="mb-4 border-b border-slate-100 pb-2">
            <ToolbarPlugin />
          </div>
        )}

        <div className="relative flex h-auto flex-col">
          <RichTextPlugin
            ErrorBoundary={LexicalErrorBoundary}
            placeholder={<EditorPlaceholder placeholder={placeholder} />}
            contentEditable={
              <div className="editor h-full" ref={onRef}>
                <ContentEditable
                  className={classNames("mb-10 h-full resize-none text-ellipsis outline-none", {
                    "min-h-20 overflow-auto lg:min-h-12": editable,
                  })}
                  onBlur={onBlur && onBlur}
                />
              </div>
            }
          />
          <TabIndentationPlugin />
          <ListPlugin />
          <ListMaxIndentLevelPlugin maxDepth={1} />
          <LinkPlugin />
          <MarkdownPlugin />
          <HashtagPlugin />
          <HorizontalRulePlugin />
          <TabFocusPlugin />
          <LexicalAutoLinkPlugin />
          <HistoryPlugin />
          <ClearEditorPlugin onClear={onClear} />
          <ResetEditorPlugin initialState={initialState} />
          <OnChangePlugin
            ignoreSelectionChange={true}
            onChange={(editorState) => {
              const bodyAsJson = JSON.stringify(editorState);
              const bodyAsPlainText = editorState.read(() => $getRoot().getTextContent());
              onChange && onChange(bodyAsJson, bodyAsPlainText);
            }}
          />
          <ClickableLinkPlugin />
          {editable && <CharacterLimitPlugin charset="UTF-8" maxLength={1024 * 8} />}
          {onSubmit && <SubmitPlugin onSubmit={onSubmit} />}
          {floatingAnchorElem && (
            <FloatingLinkEditorPlugin
              anchorElem={floatingAnchorElem}
              isLinkEditMode={isLinkEditMode}
              setIsLinkEditMode={setIsLinkEditMode}
            />
          )}
        </div>
      </div>
    </LexicalComposer>
  );
};

export default memo(Editor);
